<template>
  <div class="page-view">
    <BasePageHeader :title="$t('contractTemplate.pageTitle')" @back="$router.back()">
      <template #actions>
        <button class="btn ghost small" @click="togglePreview">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="15" height="15">
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
            <circle cx="12" cy="12" r="3"/>
          </svg>
          {{ showPreview ? $t('contractTemplate.editBtn') : $t('contractTemplate.previewBtn') }}
        </button>
      </template>
    </BasePageHeader>

    <!-- Loading -->
    <div v-if="loading" class="px-5 pt-4 flex flex-col gap-4 animate-pulse">
      <div class="h-6 w-1/3 bg-[var(--color-surface)] rounded"></div>
      <div class="h-96 bg-[var(--color-surface)] rounded-xl"></div>
    </div>

    <!-- Error -->
    <EmptyState v-else-if="loadError" :message="loadError" variant="error" />

    <div v-else class="px-5 pt-4 pb-10 flex flex-col gap-6">
      <!-- Info banner -->
      <div class="flex items-start gap-3 px-4 py-3 bg-[var(--color-surface)] border border-[var(--color-border)] rounded-xl text-sm text-[var(--color-muted)]">
        <svg class="flex-shrink-0 mt-0.5 text-[var(--color-accent)]" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16">
          <circle cx="12" cy="12" r="10"/>
          <line x1="12" y1="16" x2="12" y2="12"/>
          <line x1="12" y1="8" x2="12.01" y2="8"/>
        </svg>
        <div>
          {{ $t('contractTemplate.infoBanner') }}
          <code v-pre class="bg-[var(--color-surface-2)] px-1 rounded text-[var(--color-text)] text-xs">{{ project.name }}</code>,
          <code v-pre class="bg-[var(--color-surface-2)] px-1 rounded text-[var(--color-text)] text-xs">{{ customer.name }}</code>,
          <code v-pre class="bg-[var(--color-surface-2)] px-1 rounded text-[var(--color-text)] text-xs">{{ items }}</code>,
          <code v-pre class="bg-[var(--color-surface-2)] px-1 rounded text-[var(--color-text)] text-xs">{{ org_number }}</code>,
          <code v-pre class="bg-[var(--color-surface-2)] px-1 rounded text-[var(--color-text)] text-xs">{{ today }}</code>.
        </div>
      </div>

      <!-- Editor / Preview toggle -->
      <div class="flex flex-col gap-3">
        <div class="flex items-center justify-between">
          <h2 class="section-label m-0">{{ showPreview ? $t('contractTemplate.sectionPreview') : $t('contractTemplate.sectionHtml') }}</h2>
          <span class="text-xs text-[var(--color-muted)]">{{ template.length }} {{ $t('ui.chars') }}</span>
        </div>

        <!-- Editor -->
        <div v-if="!showPreview" class="relative">
          <BaseFormField :label="$t('contractTemplate.sectionHtml')">
            <BaseTextarea
              v-model="template"
              class="w-full font-mono text-sm leading-relaxed resize-y"
              style="min-height: 480px; tab-size: 2;"
              placeholder="<html>&#10;  <body>&#10;    <h1>{{company_name}}</h1>&#10;    ...&#10;  </body>&#10;</html>"
              spellcheck="false"
              :rows="24"
              @keydown.tab.prevent="onTab"
            />
          </BaseFormField>
        </div>

        <!-- Preview -->
        <div v-else class="border border-[var(--color-border)] rounded-xl overflow-hidden bg-white" style="min-height: 480px;">
          <iframe
            ref="previewFrame"
            class="w-full"
            style="min-height: 480px; border: none; display: block;"
            sandbox="allow-same-origin"
            :title="$t('ui.contractTemplatePreview')"
          ></iframe>
        </div>
      </div>

      <!-- Save error -->
      <div v-if="saveError" class="state-empty error text-sm">{{ saveError }}</div>

      <div class="flex justify-end gap-3">
        <button class="btn primary" :disabled="saving" @click="save">
          <svg v-if="saving" class="btn-spinner" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
          </svg>
          {{ saving ? $t('contractTemplate.saving') : $t('contractTemplate.saveBtn') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, nextTick, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useToast } from '@/composables/useToast.js';
import BasePageHeader from '@/components/base/BasePageHeader.vue';
import BaseFormField from '@/components/base/BaseFormField.vue';
import BaseTextarea from '@/components/base/BaseTextarea.vue';
import EmptyState from '@/components/base/EmptyState.vue';
import { getBranding, updateBranding } from '@/api/api.js';

const { t } = useI18n();
const toast = useToast();

const loading = ref(true);
const loadError = ref(null);
const saving = ref(false);
const saveError = ref(null);
const showPreview = ref(false);
const template = ref('');
const previewFrame = ref(null);

async function load() {
  loading.value = true;
  loadError.value = null;
  try {
    const data = await getBranding();
    template.value = data?.contract_template ?? '';
  } catch (e) {
    loadError.value = e?.data?.detail ?? e?.message ?? t('contractTemplate.loadError');
  } finally {
    loading.value = false;
  }
}

async function save() {
  saving.value = true;
  saveError.value = null;
  try {
    await updateBranding({ contract_template: template.value });
    toast.success(t('contractTemplate.savedToast'));
  } catch (e) {
    const d = e?.data;
    saveError.value = d?.contract_template?.[0] ?? d?.detail ?? e?.message ?? t('contractTemplate.saveError');
  } finally {
    saving.value = false;
  }
}

function togglePreview() {
  showPreview.value = !showPreview.value;
}

// Render template in sandboxed iframe when preview is shown
watch(showPreview, async (active) => {
  if (!active) return;
  await nextTick();
  if (previewFrame.value) {
    const doc = previewFrame.value.contentDocument;
    doc.open();
    doc.write(template.value || '<p style="padding:2rem;color:#666;">No template content yet.</p>');
    doc.close();
    // resize iframe to content height
    previewFrame.value.style.height = doc.documentElement.scrollHeight + 'px';
  }
});

// Insert a tab character at cursor position
function onTab(e) {
  const el = e.target;
  const start = el.selectionStart;
  const end = el.selectionEnd;
  template.value = template.value.slice(0, start) + '  ' + template.value.slice(end);
  nextTick(() => {
    el.selectionStart = el.selectionEnd = start + 2;
  });
}

onMounted(load);
</script>
